import {
  FondeoAsistente,
  MontoAsistente,
  OperacionAsistente,
} from '../../services/asistente-cliente.service';

/**
 * Reglas de los mensajes que el asistente deja escritos en WhatsApp cuando el
 * cliente pide un operador.
 *
 * Cada mensaje lleva quién escribe (nombre o razón social y su ID de usuario)
 * y de qué se trata, para que el operador no tenga que preguntarlo. Usa el
 * formato de WhatsApp: `*texto*` sale en negrita.
 *
 * El cliente puede editar el texto antes de enviarlo: el ID que llega no
 * prueba quién escribe. El operador lo confirma contra el teléfono registrado.
 */

/** Quién escribe. Se arma del usuario del store. */
export interface ClienteMensaje {
  nombre: string | null;
  empresa: boolean;
  usuarioId: number | null;
}

/** Motivos que van directo a WhatsApp, sin elegir nada antes. */
export type MotivoOperador = 'CUENTA' | 'OTRO';

const DECIMALES: Record<string, number> = { CLP: 0, COP: 0, USDT: 2, VES: 2, PEN: 2, USD: 2 };

export function formatearMonto(m: MontoAsistente | null): string {
  if (!m || typeof m.monto !== 'number' || !Number.isFinite(m.monto)) return '—';
  const min = DECIMALES[m.moneda] ?? 2;
  const max = m.moneda === 'USDT' ? 6 : min;
  const n = new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(m.monto);
  return `${m.moneda} ${n}`;
}

/**
 * Una cuenta es de empresa cuando trae `tipoUsuarioID`, igual que en el resto
 * del front. La empresa se nombra por su razón social; la persona, por nombre
 * y apellidos.
 */
export function clienteDeUsuario(user: any): ClienteMensaje {
  const empresa = Boolean(user?.tipoUsuarioID);
  const nombre = empresa
    ? texto(user?.razonSocial)
    : texto(`${user?.nombre ?? ''} ${user?.apellidos ?? ''}`);
  const id = Number(user?.usuarioId);
  return {
    nombre,
    empresa,
    usuarioId: Number.isInteger(id) && id > 0 ? id : null,
  };
}

/** "Hablar directo": sin motivo. */
export function mensajeGeneral(c: ClienteMensaje): string {
  return armar('Hola, necesito asistencia.', c);
}

/** "Mi cuenta" u "Otro tema": el cliente escribe su consulta al final. */
export function mensajeMotivo(motivo: MotivoOperador, c: ClienteMensaje): string {
  const tema = motivo === 'CUENTA' ? 'mi cuenta' : 'otro tema';
  return armar(`Hola, necesito asistencia con *${tema}*.`, c, [], true);
}

export function mensajeOperacion(op: OperacionAsistente, c: ClienteMensaje): string {
  const monto = op.destino
    ? `${formatearMonto(op.origen)} → ${formatearMonto(op.destino)}`
    : formatearMonto(op.origen);
  return armar('Hola, necesito ayuda con una *operación*.', c, [
    ['Operación N.º', String(op.numero)],
    ['Monto', monto],
    ['Estado', op.estadoTexto],
    ['Fecha', fechaConAnio(op.fecha)],
  ]);
}

/** "No está en la lista": el cliente cuenta de cuál habla. */
export function mensajeOperacionFueraDeLista(c: ClienteMensaje): string {
  return armar('Hola, necesito ayuda con una *operación* que no aparece en mi lista.', c, [], true);
}

export function mensajeFondeo(f: FondeoAsistente, c: ClienteMensaje): string {
  return armar('Hola, necesito ayuda con un *fondeo*.', c, [
    ['Referencia', f.referencia],
    ['Monto', f.monto ? formatearMonto(f.monto) : null],
    ['Medio', f.medio],
    ['Estado', f.estadoTexto],
    ['Fecha', fechaConAnio(f.fecha)],
  ]);
}

export function mensajeFondeoFueraDeLista(c: ClienteMensaje): string {
  return armar('Hola, necesito ayuda con un *fondeo* que no aparece en mi lista.', c, [], true);
}

/** "¿No ves tu fondeo?" → "Sigo sin verlo". */
export function mensajeNoVesFondeo(ultimo: FondeoAsistente | null, c: ClienteMensaje): string {
  const resumen = ultimo
    ? [ultimo.referencia, ultimo.monto ? formatearMonto(ultimo.monto) : null, fechaConAnio(ultimo.fecha)]
        .filter((p) => !!p)
        .join(' · ')
    : 'ninguno';
  return armar('Hola, hice un *fondeo* y no lo veo en mi saldo.', c, [
    ['Último fondeo registrado', resumen],
  ]);
}

/** "05/10/2026 14:32", en la hora del teléfono. Vacío si no hay fecha válida. */
export function fechaConAnio(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// -----------------------------------------------------------------------------

/**
 * Saludo, línea en blanco, quién escribe y los datos del caso, una línea
 * `*Campo:* valor` cada uno. Un dato vacío no sale. `consulta` cierra con
 * "Mi consulta es: " para que el cliente escriba la suya en WhatsApp.
 */
function armar(
  saludo: string,
  c: ClienteMensaje,
  datos: [string, string | null][] = [],
  consulta = false,
): string {
  const lineas: [string, string | null][] = [
    [c.empresa ? 'Empresa' : 'Cliente', c.nombre],
    ['ID de usuario', c.usuarioId != null ? String(c.usuarioId) : null],
    ...datos,
  ];
  const cuerpo = lineas
    .map(([campo, valor]) => [campo, texto(valor)] as const)
    .filter(([, valor]) => !!valor)
    .map(([campo, valor]) => `*${campo}:* ${valor}`);

  const partes = [saludo];
  if (cuerpo.length) partes.push(cuerpo.join('\n'));
  if (consulta) partes.push('Mi consulta es: ');
  return partes.join('\n\n');
}

/** Sin espacios de más; `null` si no queda nada. */
function texto(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.replace(/\s+/g, ' ').trim();
  return t || null;
}
